import { captureError, setUser } from '@smg-real-estate/browser-rum';
import { type ActionContext } from 'vuex';

import { fetchUserDetails, UserDetails } from '@/api/user';
import { isExpirationDateValid, twoDaysInTheFuture } from '@/helpers/Mfa/mfaModalHelper';
import { SourcePlatform, UserGroup, UserType, UserTypeLogin, type AuthorizedUser } from '@/types';

const { VUE_APP_SMG_API_URL } = import.meta.env;

export const InitialState = 'INITIAL_STATE';

function getInitialState(): AuthorizedUser {
  return {
    userId: InitialState,
    auth0Id: '',
    email: '',
    logonId: '',
    groups: [],
    picture: undefined,
    name: undefined,
    userType: undefined,
    sourcePlatform: SourcePlatform.SMG,
    userDetails: undefined,
    mfaSkipped: false,
  };
}

const getters = {
  getUser: (state: AuthorizedUser): AuthorizedUser => state,
  getUserId: (state: AuthorizedUser): string => state.userId,
  getAuth0Id: (state: AuthorizedUser): string => state.auth0Id,
  getEmail: (state: AuthorizedUser): string => state.email,
  getLogonId: (state: AuthorizedUser): string => state.logonId,
  getUserTypeLogin: (state: AuthorizedUser): UserTypeLogin | undefined =>
    state.groups.includes(UserGroup.CUSTOMER_SERVICE_AGENT)
      ? UserTypeLogin.SMG_INTERNAL
      : UserTypeLogin.AGENCY,
  getUserGroups: (state: AuthorizedUser): UserGroup[] => state.groups,
  hasCCCGroup: (state: AuthorizedUser): boolean =>
    state.groups.includes(UserGroup.CUSTOMER_SERVICE_AGENT),
  hasUserGroup:
    (state: AuthorizedUser) =>
    (group: UserGroup): boolean => {
      return state.groups.includes(group);
    },
  getSourcePlatform: (state: AuthorizedUser): SourcePlatform | undefined => state.sourcePlatform,
  getUserDetails: (state: AuthorizedUser): UserDetails => state.userDetails as UserDetails,
  hasMfa: (state: AuthorizedUser): boolean => state.userDetails?.requiredMFA === true,
  showMfaToggle: (state: AuthorizedUser): boolean => state.userDetails?.exemptFromMFA === true,
};

const mutations = {
  setUser(state: AuthorizedUser, user: AuthorizedUser): void {
    state.userId = user.userId;
    state.auth0Id = user.auth0Id;
    state.email = user.email;
    state.logonId = user.logonId;
    state.picture = user?.picture;
    state.name = user?.name;
    state.groups = user.groups;
  },
  setUserType(state: AuthorizedUser, userType: UserType): void {
    state.userType = userType;
  },
  setUserDetails(state: AuthorizedUser, userDetails: UserDetails): void {
    state.userDetails = userDetails;
  },
  setMfaSkipped(state: AuthorizedUser, mfaSkipped: boolean): void {
    const localStorageItem = localStorage.getItem(`mfaSkipped_` + state.userId);
    let sessionObject = JSON.parse(localStorageItem || '{}');

    state.mfaSkipped = mfaSkipped;

    if (state.mfaSkipped) {
      // set new item with new timestamp for 2 days in the future
      // if token was not set yet or expired
      if (!isExpirationDateValid(sessionObject?.expiresAt)) {
        sessionObject = {
          expiresAt: twoDaysInTheFuture(),
        };
      }
      localStorage.setItem(`mfaSkipped_` + state.userId, JSON.stringify(sessionObject));
    }
  },
};

const actions = {
  async setUserData(
    context: ActionContext<AuthorizedUser, any>,
    user: AuthorizedUser,
  ): Promise<void> {
    if (context.state.userId !== InitialState) return;
    try {
      context.commit('setUser', user);

      const localStorageItem = localStorage.getItem(`mfaSkipped_` + user.userId);
      const sessionObject = JSON.parse(localStorageItem || '{}');

      // set mfaSkipped to true if token is still valid
      context.commit('setMfaSkipped', isExpirationDateValid(sessionObject?.expiresAt));

      setUser({
        id: user.userId,
        logonId: user.logonId,
        groups: user.groups,
      });

      return;
    } catch (e) {
      captureError(e);
    }
  },
  async setUserDetails(context: ActionContext<AuthorizedUser, any>, userId: string): Promise<void> {
    const userDetails = await fetchUserDetails(userId);
    if (userDetails) {
      context.commit('setUserDetails', userDetails);
    }
  },
};

const _default = { state: getInitialState, getters, mutations, actions };
export default _default;
